import { IWM } from "../config/urlConfig";
import { toast } from "react-toastify";

// Read the caller organization's own scraps from the contracts service.
// Scraps are no longer embedded on the organization document, so this is the
// single source of truth for an org's scrap catalog.
// GET /scraps/read?organization=&page=&limit=&type=&classification=&archived=&search=
// returns a paginated envelope: { total, page, limit, totalPages, scraps }.
export const getAllScraps = (params) => async (dispatch) => {
  dispatch({ type: "GETTING_ALL_SCRAPS" });
  // Default to the max page size so selectors/dropdowns get the full catalog.
  const query = { limit: 100, ...(params || {}) };
  IWM.get("/scraps/read", { params: query })
    .then((res) => {
      const data = res.data || {};
      dispatch({
        type: "GOT_ALL_SCRAPS",
        payload: Array.isArray(data) ? data : data.scraps || [],
        meta: {
          total: data.total,
          page: data.page,
          limit: data.limit,
          totalPages: data.totalPages,
        },
      });
    })
    .catch((error) => {
      dispatch({ type: "CANNOT_GET_ALL_SCRAPS" });
    });
};

// Create a single scrap in the shared catalog. onSuccess receives the new scrap
// so the caller can link it to the current organization.
export const createScrap = (body, onSuccess) => async (dispatch) => {
  dispatch({ type: "CREATING_SCRAP" });
  try {
    const { data } = await IWM.post("/scraps/create", body);
    dispatch({ type: "CREATED_SCRAP", payload: data });
    toast.success("Scrap created successfully");
    dispatch(getAllScraps());
    if (onSuccess) onSuccess(data);
  } catch (error) {
    dispatch({ type: "CANNOT_CREATE_SCRAP" });
    toast.error(error.response ? error.response?.data : error.message);
  }
};

export const updateScrap = (id, body, onSuccess) => async (dispatch) => {
  dispatch({ type: "UPDATING_SCRAP" });
  try {
    const { data } = await IWM.put(`/scraps/update/${id}`, body);
    dispatch({ type: "UPDATED_SCRAP", payload: data });
    toast.success("Scrap updated successfully");
    dispatch(getAllScraps());
    if (onSuccess) onSuccess(data);
  } catch (error) {
    dispatch({ type: "CANNOT_UPDATE_SCRAP" });
    toast.error(error.response ? error.response?.data : error.message);
  }
};

export const deleteScrap = (id, onSuccess) => async (dispatch) => {
  dispatch({ type: "DELETING_SCRAP" });
  try {
    await IWM.delete(`/scraps/update/${id}`);
    dispatch({ type: "DELETED_SCRAP", payload: id });
    toast.success("Scrap deleted successfully");
    dispatch(getAllScraps());
    if (onSuccess) onSuccess(id);
  } catch (error) {
    dispatch({ type: "CANNOT_DELETE_SCRAP" });
    toast.error(error.response ? error.response?.data : error.message);
  }
};

// Upload an excel file of scraps. onSuccess receives the server result
// (including createdScraps ids) so the caller can link them to the org.
export const bulkUploadScraps = (file, onSuccess, onError) => (dispatch) => {
  dispatch({ type: "BULK_UPLOADING_SCRAPS" });

  const formData = new FormData();
  formData.append("file", file);

  IWM.post("/scraps/bulk/upload", formData)
    .then((res) => {
      const { successCount = 0, failedCount = 0 } = res.data || {};
      toast.success(
        `Import finished: ${successCount} added, ${failedCount} failed`
      );
      dispatch({ type: "BULK_UPLOADED_SCRAPS", payload: res.data });
      dispatch(getAllScraps());
      if (onSuccess) onSuccess(res.data);
    })
    .catch((error) => {
      dispatch({ type: "CANNOT_BULK_UPLOAD_SCRAPS" });
      toast.error(error.response ? error.response?.data : error.message);
      if (onError) onError(error);
    });
};

export const getScrapBulkUploadHistory = (params) => (dispatch) => {
  dispatch({ type: "GETTING_SCRAP_BULK_HISTORY" });
  IWM.get("/scraps/bulk/history", { params })
    .then((res) => {
      // server returns { total, page, ..., history: [...] }
      dispatch({
        type: "GOT_SCRAP_BULK_HISTORY",
        payload: res.data?.history || res.data || [],
      });
    })
    .catch((error) => {
      dispatch({ type: "CANNOT_GET_SCRAP_BULK_HISTORY" });
    });
};

export const downloadScrapTemplate = () => () => {
  IWM.get("/scraps/bulk/template", { responseType: "blob" })
    .then((res) => {
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", "scrap-bulk-upload-template.xlsx");
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    })
    .catch((error) => {
      toast.error(error.response ? error.response?.data : error.message);
    });
};

export const downloadScrapErrorReport = (id, fileLabel) => () => {
  IWM.get(`/scraps/bulk/history/${id}/error-report`, { responseType: "blob" })
    .then((res) => {
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `failed-${fileLabel || id}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    })
    .catch(async (error) => {
      let message = error.message;
      try {
        if (error.response?.data) message = await error.response.data.text();
      } catch (e) {
        /* ignore */
      }
      toast.error(message);
    });
};
